import { JSX } from 'react'

import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { SelectProps } from 'app/components/base/Select'
import Stack, { StackProps } from 'app/components/base/Stack'
import SelectField from 'app/components/fields/SelectField'
import SwitchField from 'app/components/fields/SwitchField'
import {
  getCategories,
  getMakes,
  getSubCategories
} from 'app/services/Api/listing'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { MAKE_SOURCE_TYPE } from 'app/utils/constants/listing.constants'
import { clearFieldValueIfNotFound } from 'app/utils/helpers/form.helpers'
import { getSubcategoryHeader } from 'app/utils/helpers/listings.helpers'

import useProductFieldsController from '../../hooks/useProductFieldsController'
import useProductFieldsOptions from '../../hooks/useProductFieldsOptions'

type ProductsFieldsProps = {
  selectsVariant?: SelectProps['variant']
  partNumbers?: string[]
  additionalActions?: JSX.Element
  stackSx?: StackProps['sx']
}

const ProductsFields: RFC<ProductsFieldsProps> = ({
  selectsVariant = 'select',
  additionalActions,
  stackSx
}) => {
  const { formatMessage } = useIntl()

  const { getValues, setValue } = useFormContext()

  const [
    byMakeName,
    byCategoryId,
    byModelName,
    byGenerationName,
    bySeriesName,
    byTrimName,
    bySubCategoryId
  ] = getValues([
    'by_make_name',
    'by_category_id',
    'by_model_name',
    'by_generation_name',
    'by_serie_name',
    'by_trim_name',
    'by_sub_category_id'
  ])

  const { generationOptions, modelOptions, seriesOptions, trimOptions } =
    useProductFieldsOptions({
      byGenerationName,
      byMakeName,
      byModelName,
      bySeriesName,
      byTrimName,
      setValue
    })

  useProductFieldsController({
    byGenerationName,
    byMakeName,
    byModelName,
    bySeriesName,
    setValue,
    byCategoryId
  })

  //TODO: change part numbers select to search text field
  // const partNumberOptions = useMemo(
  //   () =>
  //     partNumbers?.filter(Boolean)?.map((partNumber) => ({
  //       value: partNumber
  //     })),
  //   [partNumbers]
  // )

  return (
    <Stack spacing={2} sx={stackSx}>
      {/* <SelectField
        name="search_by_part_number"
        variant={selectsVariant}
        sortOption
        withFilter
        divider
        label={intl.formatMessage({ defaultMessage: 'Part Number' })}
        optionSettings={{
          loadOptions: undefined,
          optionValue: 'value',
          optionLabel: 'value',
          options: partNumberOptions
        }}
      /> */}

      <SwitchField
        name="not_sold_out"
        formControlProps={{
          label: formatMessage({ defaultMessage: 'Hide Sold Out' }),
          type: 'switch',
          sx: { maxWidth: { xs: '100%', md: 155 }, height: 22 }
        }}
      />

      <SelectField
        name="by_make_name"
        variant={selectsVariant}
        sortOption
        withFilter
        divider={IS_NATIVE_APP}
        label={formatMessage({ defaultMessage: 'Make' })}
        optionSettings={{
          key: 'by_make_name',
          loadOptions: () => getMakes([MAKE_SOURCE_TYPE.imported]),
          optionValue: 'name',
          optionLabel: 'name'
        }}
      />

      <SelectField
        name="by_model_name"
        variant={selectsVariant}
        sortOption
        withFilter
        divider={IS_NATIVE_APP}
        label={formatMessage({ defaultMessage: 'Model' })}
        disabled={!byMakeName}
        optionSettings={{
          loadOptions: undefined,
          options: modelOptions
        }}
      />

      <SelectField
        name="by_generation_name"
        variant={selectsVariant}
        sortOption
        withFilter
        divider={IS_NATIVE_APP}
        label={formatMessage({ defaultMessage: 'Generation' })}
        disabled={!byModelName}
        optionSettings={{
          loadOptions: undefined,
          options: generationOptions
        }}
      />

      <SelectField
        name="by_serie_name"
        variant={selectsVariant}
        sortOption
        withFilter
        divider={IS_NATIVE_APP}
        label={formatMessage({ defaultMessage: 'Series' })}
        disabled={!byGenerationName}
        optionSettings={{
          loadOptions: undefined,
          options: seriesOptions
        }}
      />

      <SelectField
        name="by_trim_name"
        variant={selectsVariant}
        sortOption
        withFilter
        divider={IS_NATIVE_APP}
        label={formatMessage({ defaultMessage: 'Trim' })}
        disabled={!bySeriesName}
        optionSettings={{
          loadOptions: undefined,
          options: trimOptions
        }}
      />

      <SelectField
        name="by_category_id"
        variant={selectsVariant}
        sortOption
        withFilter
        divider={IS_NATIVE_APP}
        label={formatMessage({ defaultMessage: 'Item Category' })}
        optionSettings={{
          key: 'category',
          loadOptions: getCategories,
          optionValue: 'id',
          optionLabel: 'name'
        }}
      />

      <SelectField
        name="by_sub_category_id"
        variant={selectsVariant}
        sortOption
        withFilter
        divider={IS_NATIVE_APP}
        groupBy={getSubcategoryHeader}
        label={formatMessage({ defaultMessage: 'Sub-Category' })}
        disabled={!byCategoryId}
        optionSettings={{
          key: byCategoryId,
          enabled: !!byCategoryId,
          loadOptions: async () => {
            const resp = await getSubCategories({
              by_category_id: byCategoryId
            })

            clearFieldValueIfNotFound({
              name: 'by_sub_category_id',
              value: null,
              setValue,
              data: resp,
              findKey: 'id',
              findValue: bySubCategoryId
            })

            return resp
          },
          optionValue: 'id',
          optionLabel: 'name'
        }}
      />

      {additionalActions}
    </Stack>
  )
}

export default ProductsFields
